<template>
  <div>
    <section class="bridge-card">
      <header v-if="!sharedCatalog" class="bridge-header">
        <div>
          <h2 class="bridge-title">Attach features to products</h2>
          <p class="bridge-description">
            Reads features, products and attachments live from Stripe. Changes are written to
            Stripe, then the affected product is re-mirrored to Postgres automatically.
          </p>
        </div>
        <div v-if="!sharedCatalog" class="bridge-toolbar">
          <aha-status-badge
            v-if="mode"
            :look="mode === 'live' ? 'alert' : 'quiet'"
            :aria-label="modeLabel + ' mode'"
          >{{ modeLabel }} mode</aha-status-badge>
          <label class="bridge-switch">
            <aha-switch
              size="small"
              aria-label="Show archived"
              :checked.prop="includeArchived"
              :disabled.prop="isLoading || !!busyKey"
              @change="toggleArchived"
            ></aha-switch>
            Show archived
          </label>
          <aha-button size="sm" :disabled="isLoading || !!busyKey" :loading="isLoading" @click="loadCatalog">Reload</aha-button>
        </div>
      </header>

      <aha-alert v-if="notice" type="warning" closable class="bridge-block" @close="notice = ''">{{ notice }}</aha-alert>

      <p v-if="isLoading && !loaded" class="bridge-muted" role="status">Loading the catalogue from Stripe</p>
      <aha-alert v-else-if="loadError" type="error" class="bridge-block">
        {{ loadError }}
        <aha-button slot="action" size="sm" @click="loadCatalog">Try again</aha-button>
      </aha-alert>
      <template v-else-if="loaded">
        <p v-if="!features.length && !products.length" class="bridge-muted">
          Stripe returned no features or products.
        </p>
        <template v-else>
          <form v-if="!hideCreate" class="bridge-block bridge-create" novalidate @submit.prevent="askCreate">
            <h3 class="bridge-subtitle">Create a feature</h3>
            <p class="bridge-muted">
              Creates the feature in Stripe, so it also appears in the Stripe dashboard, then mirrors it to Postgres.
            </p>
            <div class="bridge-create-fields">
              <div class="bridge-field">
                <span class="bridge-label">Name</span>
                <aha-input
                  aria-label="Name"
                  placeholder="For example, Audience size 50"
                  maxlength="80"
                  :value="newFeature.name"
                  :disabled.prop="!!busyKey"
                  @input="newFeature.name = detailValue($event, newFeature.name)"
                ></aha-input>
              </div>
              <div class="bridge-field">
                <span class="bridge-label">Lookup key</span>
                <aha-input
                  aria-label="Lookup key"
                  placeholder="category.key"
                  maxlength="80"
                  :value="newFeature.lookupKey"
                  :status="lookupKeyError ? 'error' : 'default'"
                  :error-message="lookupKeyError"
                  :info-message="lookupKeyError ? '' : lookupKeyHint"
                  :disabled.prop="!!busyKey"
                  @input="newFeature.lookupKey = detailValue($event, newFeature.lookupKey)"
                ></aha-input>
              </div>
              <aha-button
                variant="secondary"
                :disabled="!canCreate || !!busyKey"
                @click="askCreate"
              >
                <span slot="icon"><aha-icon name="system-plus" size="16" decorative></aha-icon></span>
                Create feature
              </aha-button>
            </div>
          </form>

          <aha-segmented
            class="bridge-block"
            aria-label="View"
            :options="viewOptionsJson"
            :value="view"
            :disabled.prop="!!busyKey"
            @change="switchView($event.detail && $event.detail.value)"
          ></aha-segmented>

          <div class="bridge-field bridge-block bridge-narrow">
            <span class="bridge-label">{{ isFeatureView ? 'Feature' : 'Product' }}</span>
            <aha-select
              :aria-label="isFeatureView ? 'Feature' : 'Product'"
              :placeholder="isFeatureView ? 'Select a feature' : 'Select a product'"
              :options="selectOptions(primaryOptions)"
              :value="selected ? selected.id : ''"
              :disabled.prop="!!busyKey"
              @change="selectPrimary(findOption(primaryOptions, $event))"
            ></aha-select>
          </div>

          <template v-if="selected">
            <h3 class="bridge-subtitle">
              {{ isFeatureView ? 'Products with this feature' : 'Features on this product' }}
              ({{ attachedItems.length }})
            </h3>
            <p v-if="!attachedItems.length" class="bridge-muted">
              {{ isFeatureView ? 'This feature is not attached to any product.' : 'This product has no features.' }}
            </p>
            <aha-list v-else class="bridge-block" bordered :items="attachedRowsJson" @action="onRowAction"></aha-list>

            <div class="bridge-field bridge-attach-picker">
              <span class="bridge-label">{{ isFeatureView ? 'Attach to products' : 'Attach features' }}</span>
              <stripe-bridge-multi-picker
                :key="`${view}:${selected.id}`"
                v-model="toAttach"
                :label="isFeatureView ? 'Products' : 'Features'"
                :placeholder="isFeatureView ? 'Search by product name or id' : 'Search by feature name or lookup key'"
                :options="attachPickerOptions"
                :attached-ids="attachedIds"
                :errors="itemErrors"
                :disabled="!!busyKey"
              ></stripe-bridge-multi-picker>
            </div>
            <aha-alert v-if="attachResult" :type="attachResult.type" class="bridge-block bridge-attach" role="status">{{ attachResult.message }}</aha-alert>
            <aha-button
              variant="primary"
              class="bridge-attach"
              :disabled="!toAttach.length || !!busyKey"
              :loading="busyKey === 'attach'"
              @click="attachSelected"
            >
              <span slot="icon"><aha-icon name="system-link" size="16" decorative></aha-icon></span>
              {{ toAttach.length > 1 ? `Attach ${toAttach.length} ${isFeatureView ? 'products' : 'features'}` : 'Attach' }}
            </aha-button>
          </template>
          <p v-else class="bridge-muted">Pick {{ isFeatureView ? 'a feature' : 'a product' }} to see its attachments.</p>
        </template>
      </template>
    </section>

    <dialog
      ref="dialog"
      class="bridge-dialog"
      aria-labelledby="bridge-dialog-title"
      @cancel.prevent="requestClose"
      @close="showConfirm = false"
    >
      <header class="bridge-dialog-header">
        <h2 id="bridge-dialog-title" class="bridge-dialog-title">{{ confirmTitle }}</h2>
        <aha-button icon-only size="xs" variant="tertiary" aria-label="Close" :disabled="!!busyKey" @click="requestClose">
          <aha-icon name="system-x" size="14" decorative></aha-icon>
        </aha-button>
      </header>
      <div class="bridge-dialog-body">
        <p v-if="pending && pending.action !== 'create'">
          Detach feature
          <strong>{{ pending.feature.name }}</strong>
          from product
          <strong>{{ pending.product.name }}</strong> in Stripe
          <aha-status-badge size="sm" :look="mode === 'live' ? 'alert' : 'quiet'">{{ modeLabel }}</aha-status-badge>?
        </p>
        <p v-else-if="pending">
          Create feature <strong>{{ pending.name }}</strong> with lookup key
          <code>{{ pending.lookupKey }}</code> in Stripe
          <aha-status-badge size="sm" :look="mode === 'live' ? 'alert' : 'quiet'">{{ modeLabel }}</aha-status-badge>?
        </p>
        <p class="bridge-muted">
          {{ pending && pending.action === 'create' ? 'The feature is then mirrored to Postgres.' : 'The product is then re-mirrored to Postgres.' }}
        </p>
        <aha-alert v-if="modalError" type="error" size="small">{{ modalError }}</aha-alert>
      </div>
      <footer class="bridge-dialog-footer">
        <aha-button :variant="isDetach ? 'primary' : 'secondary'" :disabled="!!busyKey" @click="requestClose">Cancel</aha-button>
        <aha-button
          :variant="isDetach ? 'danger' : 'primary'"
          :disabled="!!busyKey"
          :loading="!!busyKey"
          @click="runConfirmed"
        >{{ confirmOkTitle }}</aha-button>
      </footer>
    </dialog>
  </div>
</template>

<script>
import Config from '@/config'
import utils from '@/utils'
import { errorMessage, pairMethods } from './catalogMixin'
import nativeAhaElements from './nativeAhaElements'
import StripeBridgeMultiPicker from './StripeBridgeMultiPicker'

const MAX_STRIPE_TEXT_LENGTH = 80
const LOOKUP_KEY_PATTERN = /^[a-z0-9._-]{1,80}$/

export default {
  name: 'StripeBridgeAttach',
  components: { ...nativeAhaElements, StripeBridgeMultiPicker },
  mixins: [{ methods: pairMethods }],
  props: {
    hideCreate: { type: Boolean, default: false },
    sharedCatalog: { type: Object, default: null },
  },
  data() {
    return {
      mode: null,
      features: [],
      products: [],
      attachments: [],
      includeArchived: false,
      isLoading: false,
      loaded: false,
      loadError: '',
      notice: '',
      view: 'feature',
      viewOptions: [
        { value: 'feature', label: 'By feature' },
        { value: 'product', label: 'By product' },
      ],
      selected: null,
      toAttach: [],
      itemErrors: {},
      attachResult: null,
      showConfirm: false,
      pending: null,
      busyKey: '',
      modalError: '',
      newFeature: { name: '', lookupKey: '' },
      lookupKeyHint: 'Lowercase letters, digits, full stops, underscores and hyphens, up to 80 characters. Use the category.key style.',
    }
  },
  computed: {
    modeLabel() {
      return this.mode === 'live' ? 'LIVE' : 'TEST'
    },
    trimmedName() {
      return this.newFeature.name.trim()
    },
    trimmedLookupKey() {
      return this.newFeature.lookupKey.trim()
    },
    lookupKeyError() {
      const key = this.trimmedLookupKey
      return key && !LOOKUP_KEY_PATTERN.test(key)
        ? 'Use lowercase letters, digits, full stops, underscores and hyphens only, up to 80 characters.'
        : ''
    },
    canCreate() {
      return !!this.trimmedName && this.trimmedName.length <= MAX_STRIPE_TEXT_LENGTH && !!this.trimmedLookupKey && !this.lookupKeyError
    },
    isDetach() {
      return !!this.pending && this.pending.action === 'detach'
    },
    confirmTitle() {
      const titles = { detach: 'Detach this feature?', create: 'Create this feature?' }
      return this.pending ? titles[this.pending.action] : ''
    },
    confirmOkTitle() {
      const titles = { detach: 'Detach', create: 'Create' }
      return this.pending ? titles[this.pending.action] : ''
    },
    isFeatureView() {
      return this.view === 'feature'
    },
    featureById() {
      return this.indexById(this.features)
    },
    productById() {
      return this.indexById(this.products)
    },
    primaryOptions() {
      return this.isFeatureView ? this.featureOptions : this.productOptions
    },
    featureOptions() {
      return this.features.map(f => ({ id: f.id, label: f.name, sub: f.lookupKey, active: f.active }))
    },
    productOptions() {
      return this.products.map(p => ({ id: p.id, label: p.name, sub: p.id, active: p.active }))
    },
    attachedIds() {
      if (!this.selected) return []
      const key = this.isFeatureView ? 'productId' : 'featureId'
      const own = this.isFeatureView ? 'featureId' : 'productId'
      return this.attachments.filter(a => a[own] === this.selected.id).map(a => a[key])
    },
    otherOptions() {
      return this.isFeatureView ? this.productOptions : this.featureOptions
    },
    attachedItems() {
      return this.otherOptions.filter(o => this.attachedIds.includes(o.id))
    },
    attachedRows() {
      return this.attachedItems.map(item => ({
        title: item.active ? item.label : `${item.label} (archived)`,
        description: item.sub,
        actions: [{ key: item.id, icon: 'system-unlink', label: 'Detach', danger: true }],
      }))
    },
    viewOptionsJson() {
      return JSON.stringify(this.viewOptions)
    },
    attachedRowsJson() {
      return JSON.stringify(this.attachedRows)
    },
    attachPickerOptions() {
      return this.otherOptions.filter(o => o.active)
    },
  },
  watch: {
    sharedCatalog: {
      immediate: true,
      handler(catalog) {
        if (!catalog) return
        this.mode = catalog.mode
        this.features = catalog.features
        this.products = catalog.products
        this.attachments = catalog.attachments
        this.loaded = true
        const stillThere = this.selected && this.primaryOptions.find(o => o.id === this.selected.id)
        if (stillThere) this.selected = stillThere
        else {
          this.selected = null
          this.resetAttachDraft()
        }
      },
    },
  },
  created() {
    if (!this.sharedCatalog) this.loadCatalog()
  },
  methods: {
    indexById(list) {
      return Object.fromEntries(list.map(item => [item.id, item]))
    },
    selectOptions(options) {
      return JSON.stringify(
        options.map(o => ({ value: o.id, label: `${o.label} (${o.sub})${o.active ? '' : ' (archived)'}` }))
      )
    },
    findOption(options, event) {
      const value = event.detail && event.detail.value
      return options.find(o => o.id === value) || null
    },
    resetAttachDraft() {
      this.toAttach = []
      this.itemErrors = {}
      this.attachResult = null
    },
    selectPrimary(option) {
      if (this.busyKey) return
      this.selected = option
      this.resetAttachDraft()
    },
    switchView(key) {
      if (!key || key === this.view || this.busyKey) return
      this.view = key
      this.selected = null
      this.resetAttachDraft()
    },
    toggleArchived(event) {
      if (!event.detail || typeof event.detail.checked !== 'boolean') return
      this.includeArchived = event.detail.checked
      this.loadCatalog()
    },
    onRowAction(event) {
      const detail = event.detail
      if (!detail || this.busyKey) return
      const item = this.attachedItems[detail.index]
      if (item) this.askDetach(item)
    },
    async loadCatalog() {
      if (this.isLoading) return
      this.isLoading = true
      this.loadError = ''
      try {
        const res = await this.$http.get(Config.api.stripeMirrorCatalog, {
          params: { includeArchived: this.includeArchived },
        })
        this.mode = res.body.mode
        this.features = res.body.features || []
        this.products = res.body.products || []
        this.attachments = res.body.attachments || []
        this.loaded = true
        this.selected = this.selected && this.primaryOptions.find(o => o.id === this.selected.id) || null
        this.resetAttachDraft()
      } catch (error) {
        this.loadError = errorMessage(error, 'Could not load the Stripe catalogue. Please try again.')
      } finally {
        this.isLoading = false
      }
    },
    openDialog() {
      this.modalError = ''
      this.showConfirm = true
      this.$nextTick(() => {
        const dialog = this.$refs.dialog
        if (dialog && !dialog.open) dialog.showModal()
      })
    },
    closeDialog() {
      const dialog = this.$refs.dialog
      this.showConfirm = false
      if (dialog && dialog.open) dialog.close()
    },
    requestClose() {
      if (!this.busyKey) this.closeDialog()
    },
    pairFor(item) {
      return {
        feature: this.featureById[this.isFeatureView ? this.selected.id : item.id],
        product: this.productById[this.isFeatureView ? item.id : this.selected.id],
      }
    },
    askDetach(item) {
      if (!item || !this.selected) return
      this.pending = { action: 'detach', ...this.pairFor(item) }
      this.openDialog()
    },
    askCreate() {
      if (!this.canCreate || this.busyKey) return
      this.pending = { action: 'create', name: this.trimmedName, lookupKey: this.trimmedLookupKey }
      this.openDialog()
    },
    async runConfirmed() {
      if (this.busyKey) return
      this.modalError = ''
      this.notice = ''
      const outcome = this.pending.action === 'create' ? await this.runCreate() : await this.runDetach()
      if (outcome) this.closeDialog()
    },
    async runCreate() {
      const { name, lookupKey } = this.pending
      this.busyKey = 'create'
      try {
        const res = await this.$http.post(Config.api.stripeMirrorFeatures, { name, lookupKey })
        const created = res.body.feature
        this.features = [...this.features, created]
        this.newFeature = { name: '', lookupKey: '' }
        this.view = 'feature'
        this.selected = this.featureOptions.find(o => o.id === created.id) || null
        this.resetAttachDraft()
        if (res.body.mirrored === false) {
          this.notice = this.unmirroredNotice([created.name], 'created')
        } else {
          utils.showToastSuccess(`${created.name} created in Stripe and mirrored.`)
        }
        return true
      } catch (error) {
        this.modalError = errorMessage(error, 'Could not create the feature. Please try again.')
        return false
      } finally {
        this.busyKey = ''
      }
    },
    async attachSelected() {
      if (this.busyKey || !this.selected || !this.toAttach.length) return
      this.busyKey = 'attach'
      this.notice = ''
      this.itemErrors = {}
      this.attachResult = null
      const target = this.selected
      const attached = []
      const unmirrored = []
      const itemErrors = {}
      for (const id of [...this.toAttach]) {
        const { feature, product } = this.pairFor(this.otherOptions.find(o => o.id === id))
        try {
          const body = await this.attachPair(product.id, feature.id)
          this.toAttach = this.toAttach.filter(selectedId => selectedId !== id)
          const name = this.isFeatureView ? product.name : feature.name
          attached.push(name)
          if (body && body.mirrored === false) unmirrored.push(name)
        } catch (error) {
          itemErrors[id] = errorMessage(error, 'Could not attach. Please try again.')
        }
      }
      this.itemErrors = itemErrors
      this.busyKey = ''
      this.attachResult = this.summariseAttach(target, attached, Object.keys(itemErrors).length)
      if (unmirrored.length) this.notice = this.unmirroredNotice(unmirrored, 'attached')
      if (attached.length) this.$emit('changed')
    },
    summariseAttach(selected, attached, failedCount) {
      const target = selected.label
      const list = attached.join(', ')
      const done = this.isFeatureView ? `Attached ${target} to ${list}.` : `Attached ${list} to ${target}.`
      if (!failedCount) return { type: 'success', message: done }
      const kept = attached.length ? `${done} ` : ''
      return { type: 'warning', message: `${kept}${failedCount} could not be attached and stay selected below with their error.` }
    },
    async runDetach() {
      const { feature, product } = this.pending
      this.busyKey = `${product.id}:${feature.id}`
      try {
        const body = await this.detachPair(product.id, feature.id)
        this.attachResult = null
        this.$emit('changed')
        if (body && body.mirrored === false) {
          this.notice = this.unmirroredNotice([feature.name], `detached from ${product.name}`)
        } else {
          utils.showToastSuccess(`${feature.name} detached from ${product.name}. Mirror updated.`)
        }
        return true
      } catch (error) {
        this.modalError = errorMessage(error, 'Could not detach the feature. Please try again.')
        return false
      } finally {
        this.busyKey = ''
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.bridge-card {
  background: var(--aha-bg-container);
  border: 1px solid var(--aha-border-default);
  border-radius: var(--aha-radius-default);
  padding: var(--aha-space-24);
  font-family: var(--aha-font-product);
  color: var(--aha-text-default);
}

.bridge-header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--aha-space-16);
  margin-bottom: var(--aha-space-16);
}

.bridge-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--aha-space-16);
}

.bridge-switch {
  display: inline-flex;
  align-items: center;
  gap: var(--aha-space-8);
  margin: 0;
  font-size: var(--aha-size-default);
}

.bridge-title {
  margin: 0 0 var(--aha-space-4);
  font-size: var(--aha-size-h4);
  font-weight: 600;
  line-height: 1.3;
}

.bridge-subtitle {
  margin: 0 0 var(--aha-space-4);
  font-size: var(--aha-size-default);
  font-weight: 600;
  line-height: 1.3;
}

.bridge-description,
.bridge-muted {
  margin: 0 0 var(--aha-space-8);
  font-size: var(--aha-size-sm);
  line-height: 1.5;
  color: var(--aha-text-secondary);
}

.bridge-block {
  display: block;
  margin-bottom: var(--aha-space-16);
}

.bridge-create {
  border: 1px solid var(--aha-border-default);
  border-radius: var(--aha-radius-default);
  padding: var(--aha-space-16);
}

.bridge-create-fields {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: var(--aha-space-16);
}

.bridge-create-fields > .bridge-field {
  flex: 1 1 240px;
}

.bridge-create-fields > aha-button {
  margin-top: var(--aha-space-24);
}

.bridge-field {
  display: flex;
  flex-direction: column;
  gap: var(--aha-space-4);
}

.bridge-field > aha-input,
.bridge-field > aha-select {
  display: block;
  width: 100%;
}

.bridge-narrow {
  max-width: 480px;
}

.bridge-label {
  font-size: var(--aha-size-sm);
  font-weight: 600;
}

.bridge-attach {
  margin-top: var(--aha-space-8);
}

.bridge-attach-picker {
  max-width: 560px;
}

.bridge-dialog {
  width: min(520px, calc(100vw - 32px));
  padding: 0;
  border: 0;
  border-radius: var(--aha-radius-lg);
  background: var(--aha-bg-elevated);
  color: var(--aha-text-default);
  font-family: var(--aha-font-product);

  &::backdrop {
    background: var(--aha-bg-overlay);
  }
}

.bridge-dialog-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--aha-space-16);
  padding: var(--aha-space-24) var(--aha-space-24) var(--aha-space-8);
}

.bridge-dialog-title {
  margin: 0;
  font-size: var(--aha-size-h4);
  font-weight: 600;
  line-height: 1.3;
}

.bridge-dialog-body {
  display: flex;
  flex-direction: column;
  gap: var(--aha-space-8);
  padding: var(--aha-space-8) var(--aha-space-24);
  font-size: var(--aha-size-default);
  line-height: 1.5;

  p {
    margin: 0;
  }
}

.bridge-dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--aha-space-8);
  padding: var(--aha-space-16) var(--aha-space-24) var(--aha-space-24);
}
</style>
